/**
 * How many points one click on a `+` spends.
 *
 * Asked for directly: *CTRL + LEFT CLICK to allocate 5 stats points per click in character sheet.
 * Same thing for skills when allocating skill points.*
 *
 * ── Why this is a module and not two inline handlers ─────────────────────────
 *
 * There are two `+` buttons in this HUD that spend a currency a level-up granted — an attribute
 * point on the character sheet and a skill rank in the skills panel — and they are in different
 * files written months apart. A modifier that means "five" in one of them and nothing in the other
 * is worse than not having it: the player learns the gesture on one panel and it silently fails on
 * the other, which reads as the panel being broken rather than the feature being absent.
 *
 * ── Why it sends N commands rather than a count on the wire ──────────────────
 *
 * `CMD_ALLOCATE_ATTRIBUTE` and `CMD_LEVEL_SKILL` each spend exactly one point, and they stay that
 * way. Adding a count would mean a wire change, a new refusal case for "you asked for five and
 * have three", and a second place where the invariant
 * `Level × points_per_level == spent + available` has to be got right. Five commands reuse the
 * path that is already correct five times, and the server re-checks each one — so a bulk spend is
 * five ordinary spends that happen to have been asked for together, and there is no new way for it
 * to be wrong.
 *
 * The clamp against `available` is what keeps that quiet: without it, a player with three points
 * left would send two commands the server has to refuse, and each refusal is a notice on screen.
 * It is a **courtesy, not a guard** — the server is still the authority, and a client that sent
 * five anyway would simply be told no twice.
 */

/** How many points a modified click asks for. Five, because that is a level's worth of attributes. */
export const BULK_SPEND = 5;

/**
 * The number of points a click should spend, clamped to what the player actually has.
 *
 * `metaKey` as well as `ctrlKey`, because Command is the modifier a Mac user reaches for and a
 * gesture that works on one machine and not another reads as a bug in the game.
 *
 * Returns 0 when there is nothing to spend, which is the same answer as "do not send anything" —
 * so a caller can loop over it without a separate emptiness check.
 */
export function pointsForClick(
  event: { ctrlKey?: boolean; metaKey?: boolean },
  available: number,
): number {
  const wanted = event.ctrlKey || event.metaKey ? BULK_SPEND : 1;
  return Math.max(0, Math.min(wanted, Math.floor(available)));
}
